import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { track } from '@/shared/analytics/track';
import { useAuth } from '@/shared/hooks/useAuth';
import { useBrandStore } from '@/shared/stores/useBrandStore';
import LoadingSkeleton from '@/shared/components/ui/LoadingSkeleton';

interface BrandGuardProps {
  children: React.ReactNode;
}

const BrandGuard: React.FC<BrandGuardProps> = ({ children }) => {
  const { user, isLoading: isLoadingAuth } = useAuth();
  const { brandId, setBrandId } = useBrandStore();
  const [searchParams] = useSearchParams();
  const [isHydrated, setIsHydrated] = useState(false);

  const urlBrandId = searchParams.get('brandId');
  useEffect(() => {
    if (isLoadingAuth || !user) return;

    const brands = user.brands;

    // Policy 1: Single-brand users locked to their brand
    if (brands.length === 1) {
      if (brandId !== brands[0].id) {
        setBrandId(brands[0].id);
        track('brand_auto_selected', {
          to_brand_id: brands[0].id,
          to_brand_name: brands[0].name,
          reason: 'single_brand',
        });
      }
      setIsHydrated(true);
      return;
    }

    // Policy 2: Multi-brand users (URL > Store > First)
    if (brands.length > 1) {
      const isBrandValid = (id: string | null) =>
        id !== null && brands.some((b) => b.id === id);

      if (isBrandValid(urlBrandId)) {
        if (brandId !== urlBrandId) {
          setBrandId(urlBrandId);
          const matched = brands.find((b) => b.id === urlBrandId);
          track('brand_auto_selected', {
            to_brand_id: urlBrandId,
            to_brand_name: matched?.name ?? null,
            reason: 'url_param',
          });
        }
      } else if (!isBrandValid(brandId)) {
        setBrandId(brands[0].id);
        track('brand_auto_selected', {
          to_brand_id: brands[0].id,
          to_brand_name: brands[0].name,
          reason: 'first_brand',
        });
      }
    }

    setIsHydrated(true);
  }, [user, isLoadingAuth, brandId, setBrandId, searchParams]);

  // Block until hydrated
  if (!isHydrated) {
    return <LoadingSkeleton />;
  }

  return <>{children}</>;
};

export default BrandGuard;
